import { useState, type ReactNode } from "react";
import { TextField, MenuItem, Autocomplete, Typography } from "@mui/material";
import CompetenceChip from "components/CompetenceChip";
import type Competence from "constants/Competence";
import competenceMap, { competencesAssociationList } from "constants/CompetenceMap";

const options = competencesAssociationList.map((c) => ({ value: c.competence, label: c.label }));

interface CompetenceLineProps {
  competence: Competence;
  label?: string;
  noWrap?: boolean;
}
function CompetenceLine({
  competence,
  label,
  noWrap = false,
}: Readonly<CompetenceLineProps>): ReactNode {
  return (
    <Typography noWrap={noWrap}>
      <CompetenceChip competence={competence} sx={{ marginRight: 1 }} />
      {label}
    </Typography>
  );
}

interface CompetenceSelectProps {
  value?: Competence;
  onChange?: (competence?: Competence) => void;
  label?: string;
  placeholder?: string;
  size?: "small" | "medium";
  disabled?: boolean;
}

export function CompetenceSelect({
  value,
  onChange,
  label,
  placeholder,
  size = "medium",
  disabled = false,
}: Readonly<CompetenceSelectProps>): ReactNode {
  const [input, setInput] = useState<string>("");

  return (
    <Autocomplete
      value={options.find((o) => o.value === value) ?? null}
      onChange={(_e, _value) => {
        onChange?.(_value?.value ?? undefined);
      }}
      onInputChange={(_e, inputValue) => {
        setInput(inputValue);
      }}
      options={options}
      fullWidth
      autoHighlight
      openOnFocus
      selectOnFocus
      clearOnBlur
      handleHomeEndKeys
      size={size}
      getOptionLabel={({ value, label }) => `${competenceMap.get(value)?.smallLabel} ${label}`}
      renderInput={(params) => <TextField {...params} label={label} placeholder={placeholder} />}
      renderValue={(c) =>
        input === "" && <CompetenceLine competence={c.value} label={c.label} noWrap />
      }
      renderOption={(props, c) => (
        <MenuItem {...props} key={props.key}>
          <CompetenceLine competence={c.value} label={c.label} noWrap />
        </MenuItem>
      )}
      disabled={disabled}
    />
  );
}
